import { useFlag } from '@calendly/feature-check-react';
import { useBooleanState, useQueryParams } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { ArrowRoundRightIcon, DeleteIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { HostReschedule } from 'features/host_reschedule';
import { useTrack } from 'hooks/analytics_events';
import { isCalendlyMeeting } from 'shared/utils';
import { AddNotetakerButton, useNotetaker } from './add_notetaker_button';
import { AiRecapButton } from './ai_recap_button';
import { BookFollowUpMeetingButton } from './book_follow_up_meeting_button';
import { CancelMeetingModal } from './cancel_meeting_modal';
import { PendingMeetingActions } from './pending_meeting_actions';
import { RescheduleButton } from './reschedule_button';
const ADD_BUTTON_LOCATION = 'meetings_page_right_rail';
const actionsContainerCss = "a1l3occb";
const ActionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsContainerCss, props.className)} />;
});
const contentContainerCss = "c13jellu";
const ContentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentContainerCss, props.className)} />;
});
const buttonContainerCss = "b1eo2qkc";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const summaryContainerCss = "shkxmzx";
const SummaryContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(summaryContainerCss, props.className)} />;
});
const SimpleRescheduleButton = ({
  onClick
}) => <Button decoration="secondary-outline" size="small" onClick={onClick}>
    <ArrowRoundRightIcon />
    {I18n.t('publisher.action.reschedule')}
  </Button>;
const CancelButton = ({
  openCancelModal,
  sendAnalytics
}) => {
  const {
    canAdd
  } = useNotetaker();
  const handleClick = () => {
    if (sendAnalytics) sendAnalytics();
    openCancelModal();
  };
  if (canAdd) return null;
  return <Button size="small" decoration="secondary-danger" onClick={handleClick}>
      <DeleteIcon />
      {I18n.t('publisher.action.cancel')}
    </Button>;
};
const FutureMeetingActions = ({
  event,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  isNotPretending,
  inProgress,
  isCancelled,
  sendAnalyticsForReschedule
}) => {
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useBooleanState(false);
  const isGroup = event.eventType.kind === 'group';
  const isAdhoc = event.eventType.type === 'AdhocEventType';
  const {
    trigger_cancel: triggerCancel
  } = useQueryParams();
  const track = useTrack();
  useEffect(() => {
    if (triggerCancel && event.uuid === triggerCancel && isCalendlyMeeting(event)) {
      openCancelModal();
    }
  }, [triggerCancel, event, openCancelModal]);
  const showReschedule = !(isGroup || isAdhoc) && event.policy?.reschedule;
  const showCancel = event.policy?.cancel && !inProgress && !isCancelled;
  if (!isCalendlyMeeting(event)) {
    return <AddNotetakerButton event={event} buttonLocation={ADD_BUTTON_LOCATION} isNotPretending={isNotPretending} />;
  }
  const sendAnalyticsForCancel = () => track({
    name: 'Meeting Interaction',
    properties: {
      action: 'cancel',
      location: 'meetings_page_right_rail',
      event_uuid: event.uuid,
      mi_event_uuid: event.meetingIntelligence?.uuid,
      is_calendly_meeting: isCalendlyMeeting(event)
    }
  });
  return <>
      <AddNotetakerButton event={event} invitee={event.invitee} buttonLocation={ADD_BUTTON_LOCATION} isNotPretending={isNotPretending} />
      {showReschedule && <HostReschedule event={event} invitee={event.invitee} Button={SimpleRescheduleButton} sendAnalytics={sendAnalyticsForReschedule}>
          <RescheduleButton event={event} sendAnalytics={sendAnalyticsForReschedule} />
        </HostReschedule>}
      {isNotPretending && showCancel && <CancelButton event={event} openCancelModal={openCancelModal} sendAnalytics={sendAnalyticsForCancel} />}
      {!isNotPretending && showCancel && <Button size="small" decoration="secondary-danger" onClick={openCancelModal}>
            <DeleteIcon />
            {I18n.t('publisher.action.cancel')}
          </Button>}
      {isCancelModalOpen && <CancelMeetingModal event={event} onClose={closeCancelModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} showReportModal={showReportModal} />}
    </>;
};
const PastMeetingActions = ({
  event,
  isNotPretending
}) => {
  const {
    meetingIntelligence
  } = event;
  const hasSummary = meetingIntelligence && Boolean(meetingIntelligence?.summary);
  if (hasSummary) {
    return <ContextualBanner>
        <ContentContainer>
          <SummaryContainer>
            {HtmlParser(I18n.t('publisher.meetings.actions.in_this_meeting', {
            summary: meetingIntelligence?.summary
          }))}
          </SummaryContainer>
          <ButtonContainer>
            {isNotPretending && <AiRecapButtonComponent event={event} />}
            <BookFollowUpMeetingButton event={event} />
          </ButtonContainer>
        </ContentContainer>
      </ContextualBanner>;
  }
  return <>
      {isNotPretending && <AiRecapButtonComponent event={event} />}
      <BookFollowUpMeetingButton event={event} />
    </>;
};
const AiRecapButtonComponent = ({
  event
}) => {
  const hasMeetingIntelligence = useFlag('meeting_intelligence');
  if (!hasMeetingIntelligence) return null;
  return <AiRecapButton event={event} location="meetings_page_right_rail" />;
};
const ActionsLoadedComponent = ({
  event,
  isPending,
  isFutureMeeting,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  isNotPretending,
  isPast,
  isCancelled,
  inProgress
}) => {
  const {
    isLoading
  } = useNotetaker();
  if (isLoading) {
    return <ActionsContainer>
        <Loader size="small" inline="true" />
      </ActionsContainer>;
  }
  return <ActionsComponent event={event} isPending={isPending} isFutureMeeting={isFutureMeeting} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} isNotPretending={isNotPretending} isPast={isPast} isCancelled={isCancelled} inProgress={inProgress} />;
};
const ActionsComponent = ({
  event,
  isPending,
  isFutureMeeting,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  isNotPretending,
  isPast,
  isCancelled,
  inProgress
}) => {
  const track = useTrack();
  const sendAnalyticsForReschedule = () => track({
    name: 'Meeting Interaction',
    properties: {
      action: 'reschedule',
      location: 'meetings_page_right_rail',
      event_uuid: event.uuid,
      mi_event_uuid: event.meetingIntelligence?.uuid,
      is_calendly_meeting: isCalendlyMeeting(event)
    }
  });
  if (isPending) {
    return <ActionsContainer>
        <PendingMeetingActions event={event} clearSelectedEvent={clearSelectedEvent} />
      </ActionsContainer>;
  }
  const isGroup = event.eventType.kind === 'group';
  const isAdhoc = event.eventType.type === 'AdhocEventType';
  const showReschedule = !(isGroup || isAdhoc) && event.policy?.reschedule;
  return <ActionsContainer>
      {isFutureMeeting && <FutureMeetingActions event={event} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} isNotPretending={isNotPretending} inProgress={inProgress} isCancelled={isCancelled} sendAnalyticsForReschedule={sendAnalyticsForReschedule} />}
      {inProgress && <>
          <AddNotetakerButton event={event} buttonLocation={ADD_BUTTON_LOCATION} isNotPretending={isNotPretending} />
          {showReschedule && <HostReschedule event={event} invitee={event.invitee} Button={SimpleRescheduleButton} sendAnalytics={sendAnalyticsForReschedule}>
            <RescheduleButton event={event} sendAnalytics={sendAnalyticsForReschedule} />
          </HostReschedule>}
        </>}
      {isPast && <PastMeetingActions event={event} isNotPretending={isNotPretending} />}
    </ActionsContainer>;
};
export const Actions = ({
  event,
  clearSelectedEvent,
  showReportModal,
  updateSelectedEvent,
  inProgress,
  isFutureMeeting,
  isNotPretending = true,
  isPending,
  isPast,
  isCancelled
}) => {
  if (isNotPretending) {
    return <ActionsLoadedComponent event={event} isPending={isPending} isFutureMeeting={isFutureMeeting} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} isNotPretending={isNotPretending} isPast={isPast} isCancelled={isCancelled} inProgress={inProgress} />;
  }
  return <ActionsComponent event={event} isPending={isPending} isFutureMeeting={isFutureMeeting} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} isNotPretending={isNotPretending} isPast={isPast} isCancelled={isCancelled} inProgress={inProgress} />;
};

require("./actions.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./actions.js");